/**
 * @typedef {Object} Utils
 *
 * @property {function} _
 * @property {function} handleResponsePromise
 * @property {function} makeInternationalAPIUrl
 * @property {function} makeContentAPIUrl
 * @property {function} setTooltipsLibrary
 * @property {function} getTooltipById
 * @property {function} areTooltipsAvailable
 * @property {function} setSharedContent
 * @property {function} getSharedContent
 * @property {function} setRootVmReference
 * @property {function} processKeyDates
 * @property {function} getNextStartDateChronologically
 * @property {function} returnAllStartDates
 * @property {function} retrieveMetadataRecursively
 * @property {function} getRootMetadata
 * @property {function} anchorConstructorFactory
 * @property {function} sanitiseContentApiArray
 * @property {function} bindDynamicBlockReferences
 * @property {function} addGenericPostLinkTrigger
 * @property {function} mapSubpagesBlocksToContent
 * @property {function} validateCmsResponse
 * @property {function} addPhdBlockMetaIfExists
 * @property {function} convertToSlug
 *
 */


(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'Utils', [ '$q', '$interpolate', '$timeout', 'LoggerFactory', 'ErrorManager', 'SubpagesService', 'App', 'TEXTS', 'CONFIG', UtilitiesService ]);


    /**
     * Facade-like singleton providing various generics & helping methods that
     * can be used by other Services, Controllers and Directives.
     *
     * Note: All injected services *CANNOT* use this service as it would result
     * in cyclic infinite dependency!
     *
     * @param {ng.IQService} $q
     * @param {ng.IInterpolateService} $interpolate
     * @param {ng.ITimeoutService} $timeout
     * @param {LoggerFactory} LoggerFactory
     * @param {ErrorManager} ErrorManager
     * @param {SubpagesService} SubpagesService
     * @param {App} App
     * @param {Object} TEXTS
     * @param {Object} CONFIG
     *
     * @returns {Utils}
     */
    function UtilitiesService(
        $q,
        $interpolate,
        $timeout,
        LoggerFactory,
        ErrorManager,
        SubpagesService,
        App,
        TEXTS,
        CONFIG
    ){





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'Utils' );


        /** @const */
        var BLOCK_CONTENT_TYPE_PHD  = 'content-plain-phd',

        /**
         *  List and underlying codes MUST strictly follow RFCs.
         *  See https://www.w3.org/Protocols/rfc2616/rfc2616-sec10.html
         */
        HTTP_CODES = {
            NOT_FOUND:            404,
            SERVER_UNAVAILABLE:   503,
            UNKNOWN:              -1
        };


        /** @type {Object} ~ Collection with metadata to construct tooltips dynamically. */
        var tooltipsCollection  = null;

        /** @type {Object} ~ Collection with content shared across website. */
        var sharedContent       = null;

        /** @type {Object} */
        var currentRootVmObject = null;





        /* ===== PROTOTYPE DEFINITIONS ====================================== */


        /**
         * Resolves given Object Dot Notation literal ($s) to get
         * attribute's location inside provided Object's ($o) structure
         * and if the attribute exists, returns its value.
         *
         * @param {Object} o Object which attribute will be searched for.
         * @param {string} s Attribute's location in format of Object Dot
         * Notation.
         *
         * @returns {Object}
        */
        Object.byString = function( o, s ) {
            s = s.replace( /\[(\w+)\]/g, '.$1' ); // convert indexes to properties
            s = s.replace( /^\./, '' );           // strip a leading dot

            var a = s.split( '.' ), k, n, i = 0;

            for ( i = 0, n = a.length; i < n; ++i ) {
                k = a[i];
                if ( k in o ) {
                    o = o[ k ];
                } else {
                    return;
                }
            }

            return o;
        };


        /**
         * Assigns unique ID to the given object so different instances can be
         * easily identified
         */
        (function() {
            if ( angular.isUndefined( Object.id ) ) {
            var id = 0;

            Object.id = function(o) {
                if ( angular.isUndefined( o.__uniqueid ) ) {
                Object.defineProperty(o, "__uniqueid", {
                    value: ++id,
                    enumerable: false,
                    // This could go either way, depending on your
                    // interpretation of what an "id" is
                    writable: false
                });
                }

                return o.__uniqueid;
            };
            }
        })();


        /** @type {Utils} */
        var service = {
            // Template utilities
            _:                           interpolateText,

            // Request/Response utilities
            handleResponsePromise:       handleResponsePromise,
            makeInternationalAPIUrl:     makeInternationalAPIUrl,
            makeContentAPIUrl:           makeContentAPIUrl,

            // Content manipulation
            setTooltipsLibrary:          setTooltipsLibrary,
            getTooltipById:              getTooltipById,
            areTooltipsAvailable:        areTooltipsAvailable,

            setSharedContent:            setSharedContent,
            getSharedContent:            getSharedContent,

            setRootVmReference:          setRootVmReference,

            sanitiseContentApiArray:     sanitiseContentApiArray,
            processKeyDates:             processKeyDates,

            getNextStartDateChronologically: getNextStartDateChronologically,
            returnAllStartDates: returnAllStartDates,

            retrieveMetadataRecursively: retrieveMetadataRecursively,
            getRootMetadata:             getRootMetadata,

            // Content adjustments
            bindDynamicBlockReferences:  bindDynamicBlockReferences,
            anchorConstructorFactory:    anchorConstructorFactory,

            addPhdBlockMetaIfExists:     addPhdBlockMetaIfExists,
            convertToSlug:               convertToSlug,

            // Controller binders
            addGenericPostLinkTrigger:   addGenericPostLinkTrigger,
            mapSubpagesBlocksToContent:  mapSubpagesBlocksToContent,

            // Validators
            validateCmsResponse:         validateCmsResponse
        };



        ////////////////////////////////////////////////////////////////////

        return service; // Expose API

        ////////////////////////////////////////////////////////////////////



        /* ===== PUBLIC METHODS ========================================= */


        /**
         * Allows subcomponents to attach their own function
         * to `externalPostLink` hook available on the Controller.
         *
         * This function gets called immediately after Controller
         * renders all its subcomponents. This can be helpful when
         * trying to access subcomponents synchronously, after their
         * rendering is complete, with a logic contained within
         * those subcomponents.
         *
         * @param {ng.IController} vmObject
         * @param {function} postLinkCallback
         */
        function addGenericPostLinkTrigger( vmObject, postLinkCallback ) {
            vmObject.$postLink = function() {
                $timeout( function() {
                    if ( vmObject.externalPostLink ) vmObject.externalPostLink();
                    if ( postLinkCallback ) postLinkCallback();
                }, 0 );
            }
            vmObject.externalPostLink = undefined;
        }


        /**
         * Accepts `vm.content` Object, scans through all the subpages content
         * information and if one of the dynamic blocks contains a 'PhD block'
         * and a `postgrad-intro` block in the 'About' subpage does exist, it
         * creates a metadata that can be used within the `postgrad-intro` to
         * automatically create a link to the dynamic block.
         *
         * @param {Object} contentObject
         */
        function addPhdBlockMetaIfExists( contentObject ) {
            // Inject meta information if 'PhD' block AND 'Intro Block' do exist
            Object.keys( contentObject ).forEach( function( tabContentKey ) {
                contentObject[ tabContentKey ].some( function( singleBlockContent ) {
                    if ( singleBlockContent.contentType === BLOCK_CONTENT_TYPE_PHD && contentObject.hasOwnProperty( 'AboutBlocks' ) ) {
                        var phdBlock        = singleBlockContent,
                        introPostgradBlock  = getDynamicBlockByContentType( contentObject[ 'AboutBlocks' ], 'intro-postgrad' )

                        phdBlock.tabId      = tabContentKey;

                        if ( introPostgradBlock ) {
                            introPostgradBlock.asset_contents.phd = phdBlock;
                        } else {
                            logger.warn( 'There is no `intro-postgrad` dynamic block on the `About` page, so `' + BLOCK_CONTENT_TYPE_PHD + '` block has nowhere to be linked from!' );
                        }
                    }
                });
            });
        }


        /**
         * After {Promise} provided by $resource is resolved, analyse received
         * results, process them and return new {Promise} (to maintain
         * chainability).
         *
         * @param {Promise<any>} responsePromise Promise returned by $resource
         * after making the HTTP request.
         *
         * @returns {Promise<any>}
         */
        function handleResponsePromise( responsePromise ) {

            return responsePromise.then(
                function( okResponse ) {

                    // Success!
                    logger.debug( "HTTP response successfully retrieved!", okResponse );

                    return okResponse;
                },
                function( errResponse ) {
                    // Fail!
                    var errMsg = resolveFailedResponse( errResponse );
                    return $q.reject( errMsg );
                }
            );
        }


        /**
         * Constructs full URL (including protocol, domain etc.) of
         * Squiz (Content) API from provided endpoint part.
         *
         * @param {[string]} endpointPath
         *                  ~ API request endpoint URL path.
         * @param {string}  contentModeToEnforce
         *                  ~ ID of the content mode that should be enforce
         *                      when assembling the API request URL.
         *
         * @return {[string]} ~ Fully constructed URL.
         */
        function makeContentAPIUrl( endpointPath, contentModeToEnforce ) {
            if ( contentModeToEnforce && !CONFIG.MODES.hasOwnProperty( contentModeToEnforce.toUpperCase() )) {
                logger.warn( "Unable to enforce given content mode `%s` when assembling content API URL - it does not exist. Currently selected mode's configuration will be used...", contentModeToEnforce );
                contentModeToEnforce = null;
            }

            var contentMode = contentModeToEnforce ? CONFIG.MODES[ contentModeToEnforce.toUpperCase() ] : App.getContentMode();

            return contentMode.API.CMS.PROTOCOL + '://' + contentMode.API.CMS.BASE_URL + endpointPath;
        }


        /**
         * Constructs full URL (including protocol, domain etc.) of
         * International API from provided endpoint part.
         *
         * @param  {[string]} endpointPath ~ API request endpoint URL path.
         *                                 (Defaults to root: '/')
         *
         * @return {[string]}              ~ Fully constructed URL.
         */
        function makeInternationalAPIUrl( endpointPath ) {
            var endpointPath = endpointPath || '';

            return CONFIG.API.INTERNATIONAL.BASE_URL + '/' + endpointPath;
        }


        /**
         * Interpolates text and replaces all found {{ p[ i ] }} placeholders
         * by parameters given to this function.
         *
         * @param  {[string]} text          ~ Text including parameter placeholders.
         * @param  {[string[]]} stringParameters ~ Single or multiple parameters
         *                                       that will be injected into the
         *                                       retrieved text (instead of
         *                                       {{ p }} placeholders).
         *
         * @return {[string]}               ~ Retrieved text with injected parameters.
         */
        function interpolateText( text, stringParameters ) {
            //console.log("text:", text, stringParameters);

            var stringParameters = angular.isDefined( stringParameters ) ?  stringParameters : [];

            if ( stringParameters.length < 1 ){
                // Don't use interpolation if there are no parameters present
                return text;
            } else {
                if ( angular.isString( stringParameters ) ) stringParameters = [ stringParameters ];

                var parametersContextObject = { 'p': angular.fromJson( angular.toJson( stringParameters )) };

                // Interpolate and compile immediately
                return $interpolate( text )( parametersContextObject );
            }
        }


        /**
         * A generic helper that verifies that given response Object does
         * contain all attributes specified in `mandatoryAttributesList`.
         *
         * If it do, it returns {true}, otherwise commits an {AppError} and
         * returns {false}.
         *
         * @param {Object} responseObject
         * @param {string[]} mandatoryAttributesList
         *
         * @returns {boolean}
         */
        function validateCmsResponse( responseObject, mandatoryAttributesList ) {
            if( responseObject.error && responseObject.error.length > 0 ){

                logger.error( "Response was received with an error `%s` (code: %s)!", responseObject.error, responseObject.code, responseObject );

                ErrorManager.handleError(
                    responseObject.error,
                    'CMS_RESPONSE_FAILURE',
                    { responseCode: responseObject.code }
                );

                return false;
            } else {
                mandatoryAttributesList.forEach( function( mandatoryAttribute ) {
                    if ( !responseObject.hasOwnProperty( mandatoryAttribute ) ){

                        logger.error( "Content with missing attribute `%s` returned from the server", mandatoryAttribute, responseObject );

                        ErrorManager.handleError(
                            "We encountered an error when retrieving data from our servers. Please, try again in a moment.",
                            'CMS_RESPONSE_MALFORMATTED',
                            { missingAttribute: mandatoryAttribute }
                        );

                        return false;
                    }
                });
            }

            return true;
        }


        /**
         * Cycles through `dynamicBlocksList` and if a dynamic block has got a
         * a 'references' attribute and reference ID indeed does exist within
         * `referencesObject`, it binds the reference Object with the dynamic
         * block Object by replacing the 'references' value with a memory reference.
         *
         * @param {Object[]} referencesObject
         * @param {Object[]} dynamicBlocksList
         */
        function bindDynamicBlockReferences( referencesObject, dynamicBlocksList ) {
            var singleBlockContent,
            singleReferenceString, referenceStrings;

            dynamicBlocksList.forEach( function( singleBlock ) {
                singleBlockContent = singleBlock.asset_contents;

                if ( singleBlockContent && singleBlockContent.hasOwnProperty( 'references' ) ) {
                    referenceStrings = singleBlockContent[ 'references' ];

                    for ( var i = 0; i < referenceStrings.length; i++ ){
                        singleReferenceString = referenceStrings[ i ];

                        if ( referencesObject.hasOwnProperty( singleReferenceString ) ){
                            singleBlockContent[ singleReferenceString ] = referencesObject[ singleReferenceString ];
                        } else {
                            logger.warn( "Reference `%s` could not be attached to the given content as it does not exist on References object.", singleReferenceString );
                        }
                    }
                }
            });
        }


        /** @param {Object} tooltipsObject */
        function setTooltipsLibrary( tooltipsObject ){
            tooltipsCollection = tooltipsObject;
        }


        /**
         * @param {string} tooltipId
         *
         * @returns {(null|string)}
         */
        function getTooltipById( tooltipId ) {
            return tooltipsCollection.hasOwnProperty( tooltipId ) ? tooltipsCollection[ tooltipId ] : null;
        }


        /** @returns {boolean} */
        function areTooltipsAvailable() {
            return tooltipsCollection ? true : false;
        }


        /** @type {Object} sharedContentObject */
        function setSharedContent( sharedContentObject ){
            sharedContent = sharedContentObject;
        }


        /** @return {Object} */
        function getSharedContent() {
            return sharedContent;
        }



        /**
         * Tries to access attribute specified by `contentObjectAttribute` on
         * given Controller instance and construct
         *
         * @param {angular.IController} vm
         * @param {string} contentObjectAttribute
         * @param {string} slugFallbackStringAttribute
         *
         * @return {function}
         */
        function anchorConstructorFactory( vm, contentObjectAttribute, slugFallbackStringAttribute ) {
            var contentObject   = vm[ contentObjectAttribute ],
            anchorId            = undefined;

            /** Anchor constructor closure. */
            return function () {
                // If there's API supplied `anchor` attribute, use it
                if ( contentObject.anchor ) return contentObject.anchor;

                // Otherwise, generate one from the `heading` and assign it to the `anchorId`
                if ( anchorId ) return anchorId;

                anchorId = convertToSlug( contentObject[ slugFallbackStringAttribute ]);

                return anchorId;
            }
        }


        /**
         * If the last {Object} in the {Array} is empty, remove it.
         *
         * @param {Array} arrayToSanitise
         * @returns {void}
         */
        function sanitiseContentApiArray( arrayToSanitise ) {
            if ( arrayToSanitise.length < 1 ) return;

            if ( angular.equals( {}, arrayToSanitise[ arrayToSanitise.length - 1 ] ) ) {
                arrayToSanitise.splice( -1 );
            }
        }


        /**
         * Returns chronological next domestic `Starting Date` in
         * ISO format.
         *
         * @param {Array[Object]} keyDatesSet
         *
         * @return {String}
         */
        function getNextStartDateChronologically( keyDatesSet ) {
            var dateNow   = new Date(),
            nextStartDate = null;

            keyDatesSet.forEach( function( keyDateObject ) {
                if ( keyDateObject.domestic && keyDateObject.hasOwnProperty( 'startDate' ) && keyDateObject.startDate.hasOwnProperty( 'date' )) {
                    if ( new Date( keyDateObject.startDate.date ).getTime() > dateNow.getTime() && ( !nextStartDate || new Date( nextStartDate ).getTime() > new Date( keyDateObject.startDate.date ) ) ) {
                        nextStartDate = keyDateObject.startDate.date;
                    }
                }
            });

            return nextStartDate;
        }

        /**
         * Returns next domestic `Starting Dates` in
         * ISO format.
         *
         * @param {Array[Object]} keyDatesSet
         *
         * @return {Array}
         * Funnelback accepts pipe delimeted separators
         */
        function returnAllStartDates( keyDatesSet ) {
            var dateNow   = new Date(),
            dateArray = [];

            keyDatesSet.forEach( function( keyDateObject ) {
                if ( keyDateObject.domestic && keyDateObject.hasOwnProperty( 'startDate' ) && keyDateObject.startDate.hasOwnProperty( 'date' )) {
                    if ( new Date( keyDateObject.startDate.date ).getTime() > dateNow.getTime() ) {
                        var currDate = new Date(keyDateObject.startDate.date);
                        currDate.setDate(currDate.getDate() + 1);
                        
                        dateArray.push(currDate.toISOString().split('T')[0].replace(/-/g,''));
                    }
                }
            });

            if (dateArray.length > 0){
                return (dateArray.slice(0, 3).join("|"))
            }
            else {
                return null;
            }
        }


        /**
         * Sorts 'key dates' into `domestic` and `international` based on
         * the internal flags, time and date.
         *
         * @param {Array[Object]} keyDatesSet
         *
         * @return {{domestic:string, international:string}}
         */
        function processKeyDates( keyDatesSet ) {
            var domesticDates  = [],
            internationalDates = [];

            var currentDate = new Date().toISOString();

            sanitiseContentApiArray( keyDatesSet );

            keyDatesSet.sort( function compareDates( firstDate, secondDate ) {
                if ( !firstDate.hasOwnProperty( 'startDate' ) ) return 1; // If the `startDate` is not available, it's automatically prioritised

                if ( firstDate.startDate.date < secondDate.startDate.date )
                    return -1;
                if ( firstDate.startDate.date > secondDate.startDate.date )
                    return 1;
                return 0;
            });

            keyDatesSet.forEach( function( keyDate ) {
                // If the start date already happened, do not use.
                if ( keyDate.hasOwnProperty( 'startDate' ) && keyDate.startDate.date < currentDate ) return;

                // If not, assign into international ...
                if ( keyDate.hasOwnProperty( 'international' ) && keyDate.international ) {
                    if ( internationalDates.length == 0 ) keyDate.highlighted = true;

                    internationalDates.push( keyDate );
                }

                // ... or domestic
                if ( keyDate.hasOwnProperty( 'domestic' ) && keyDate.domestic ) {
                    if ( keyDate.hasOwnProperty( 'applyDate' ) && keyDate.applyDate.date < currentDate ) {
                        keyDate.lateApplyPossible = true;
                    }

                    if ( domesticDates.length == 0 ) keyDate.highlighted = true;

                    domesticDates.push( keyDate );
                }
            });

            demoteTrimesterThreeInDegrees( domesticDates );
            demoteTrimesterThreeInDegrees( internationalDates );

            function demoteTrimesterThreeInDegrees( dateSet ) {
                // Not a degree/ -> Don't demote anything
                if ( !SubpagesService.isDegreeSubpage() || dateSet < 2 ) return;

                // is a degree/ -> Demote (swap) 3rd trimester with 1st trimester next year, only *if* the 3rd trimester is the next date
                if ( dateSet[ 0 ].hasOwnProperty( 'applyDate') && dateSet[ 0 ].applyDate.trimester === "3" ) {
                    var keyDateToPromote =  dateSet[ 1 ];
                    keyDateToPromote.highlighted = true;

                    // Swap
                    dateSet[ 1 ] = dateSet[ 0 ];
                    dateSet[ 0 ] = keyDateToPromote;
                }
            }

            return {
                domestic:       domesticDates,
                international:  internationalDates
            };
        }


        /**
         * Turns standard (random) text into a standardised slug that can be
         * safely used in programming languages, URLs etc.
         *
         * @param {string} text
         *
         * @returns {string}
         */
        function convertToSlug( text ) {
            if ( !text ) return;

            return text.toString().toLowerCase().trim()
                .replace(/&/g, '-and-')         // Replace & with 'and'
                .replace(/[\s\W-]+/g, '-')      // Replace spaces, non-word characters and dashes with a single dash (-)
        }


        function mapSubpagesBlocksToContent( responseSubpagesObject, contentObject ) {
            for ( var subpageId in responseSubpagesObject ) {
                if ( responseSubpagesObject.hasOwnProperty( subpageId )) {
                    contentObject[ subpageId.toLowerCase() + 'Blocks' ] = responseSubpagesObject[ subpageId ].asset_contents;
                }
            }
        }


        /**
         * Traverses the `componentScope`'s parents up the chain until it finds
         * `metadataKey` object ('metadata' by default).
         *
         * If the object does contain `attributeKey` key, it returns its
         * value, otherwise it returns the entire `metadataKey` object itself.
         *
         * If nothing is found, returns {null}.
         *
         * @param {ng.IScope} componentScope $scope from which the traversal
         *                              should start
         * @param {string} attributeKey Name of the object inside `metadataKey`
         *                              to be retrieved.
         *                              If not defined, returns `metadataKey`
         *                              object itself.
         * @param {string} metadataKey  Name of the metadata attribute, optional
         *                              (default: `metadata`).
         *
         * @returns {null|Object}
         */
        function retrieveMetadataRecursively( componentScope, attributeKey, metadataKey ) {
            var parentalContainerScope  = componentScope,
            metadataKey                 = metadataKey || 'metadata';

            while( parentalContainerScope = parentalContainerScope.$parent ){
                if ( parentalContainerScope.hasOwnProperty( 'vm' ) && parentalContainerScope.vm.hasOwnProperty( metadataKey ) ){
                    if ( !attributeKey ) {
                        return parentalContainerScope.vm[ metadataKey ];
                    } else if ( attributeKey && parentalContainerScope.vm[ metadataKey ] && parentalContainerScope.vm[ metadataKey ].hasOwnProperty( attributeKey ) ) {
                        return parentalContainerScope.vm[ metadataKey ][ attributeKey ];
                    }
                }
            }

            return null;
        }


        function getRootMetadata() {
            if ( currentRootVmObject && currentRootVmObject.hasOwnProperty( 'metadata' ) ){
                return currentRootVmObject[ 'metadata' ];
            } else {
                return undefined;
            }
        }

        function setRootVmReference( vmReferenceObject ) {
            currentRootVmObject = vmReferenceObject;
        }





        /* ===== PRIVATE FUNCTIONS ====================================== */


        /**
         * Searches an array of dynamic blocks and if there's a block with
         * specified `contentType`, it returns it. Otherwise, it returns {null}.
         *
         * @param {Object[]} dynamicBlocksList
         * @param {string} contentType
         *
         * @returns {(null|Object)}
         */
        function getDynamicBlockByContentType( dynamicBlocksList, contentType ) {
            var foundDynamicBlock = null;

            dynamicBlocksList.forEach( function( dynamicBlock ) {
                if ( dynamicBlock.contentType === contentType ) foundDynamicBlock = dynamicBlock;
            });

            return foundDynamicBlock;
        }


        /**
         * Produces error message and useful logs when given
         * result object returned  by (un)successful $http call.
         *
         * @param {Object} responseObject Response object returned by $resource.
         *
         * @returns {string}
         */
        function resolveFailedResponse( responseObject ) {
            var errMsg;

            switch( responseObject.status ) {
                case HTTP_CODES.NOT_FOUND: {
                    errMsg = "Sorry, but URL " + responseObject.config.url + " you are trying to connect to does not exist.";

                    logger.error( "DataService could not find the endpoint `" + responseObject.config.url + "` and received #404 Not Found HTTP code.", responseObject );
                } break;
                case HTTP_CODES.SERVER_UNAVAILABLE: {
                    errMsg = "We apologise, but this page is currently unavailable. Please, try it again after few minutes.";

                    logger.error( "DataService could not connect to the endpoint `" + responseObject.config.url + "` and received #503 Service Unavailable HTTP code.", responseObject );
                } break;
                case HTTP_CODES.UNKNOWN: {
                    errMsg = "We apologise, but this page is taking too long to respond. Please, try it again after few minutes.";

                    logger.error( "DataService timed out to the endpoint `" + responseObject.config.url + "` and received -1 (unknown) code.", responseObject );
                }
                // Status code === 0 or any other undefined
                default: {
                    errMsg = "Sorry, we were not able to connect to the API. Try it again. (code: " + responseObject.status + ")";

                    logger.error( "Unknown HTTP response error code: " + responseObject.status + ".", responseObject );
                }
            }

            return errMsg;
        }


    }
})();
